import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { getRecaptchaToken } from "@/lib/recaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const PHONE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const inputCls =
  "w-full bg-transparent border border-ink/20 rounded-md px-4 py-3 text-ink placeholder:text-ink/35 focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal transition-colors";

const Field = ({ label, name, type = "text", required, value, onChange, placeholder, testid, as, options, inputMode, maxLength, hint, extra }) => (
  <label className="block">
    <span className="block font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-2">
      {label} {required && <span className="text-teal">*</span>}
    </span>
    {as === "textarea" ? (
      <textarea name={name} value={value} onChange={onChange} rows={4} placeholder={placeholder}
        data-testid={testid} className={inputCls} required={required} />
    ) : as === "select" ? (
      <select name={name} value={value} onChange={onChange} data-testid={testid} className={inputCls} required={required}>
        <option value="" disabled>Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
        inputMode={inputMode} maxLength={maxLength}
        data-testid={testid} className={inputCls} required={required} />
    )}
    {hint && <span className="block mt-1.5 text-xs text-ink/45">{hint}</span>}
    {extra}
  </label>
);

// Inline OTP verify block for email / mobile
const OtpBlock = ({ channel, target, valid, verified, onVerified, testidPrefix }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setSent(false); setCode(""); setError(""); }, [target]);

  if (verified) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-teal font-600" data-testid={`${testidPrefix}-verified`}>
        <ShieldCheck className="h-4 w-4" /> Verified
      </div>
    );
  }

  const send = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/otp/send`, { channel, target });
      setSent(true);
      if (r.data?.dev_code) setCode(r.data.dev_code);
      toast.success(channel === "email" ? "Code sent to your email" : "Code sent by SMS");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not send the code — try again");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/otp/verify`, { channel, target, code });
      onVerified(r.data.token);
      toast.success("Verified");
    } catch (e) {
      setError(e.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 space-y-2">
      {!sent ? (
        <button type="button" onClick={send} disabled={!valid || busy} data-testid={`${testidPrefix}-send-otp`}
          className="text-sm font-display font-600 rounded-md px-4 py-2 border border-ink/20 bg-paper hover:border-teal transition-colors disabled:opacity-50">
          {busy ? "Sending…" : "Send verification code"}
        </button>
      ) : (
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric" placeholder="Enter code" data-testid={`${testidPrefix}-otp-input`}
            className={`${inputCls} !py-2 flex-1`} />
          <button type="button" onClick={verify} disabled={code.length < 4 || busy} data-testid={`${testidPrefix}-verify-otp`}
            className="text-sm font-display font-600 rounded-md px-4 py-2 bg-teal text-ink hover:bg-teal-bright transition-colors disabled:opacity-50">
            {busy ? "…" : "Verify"}
          </button>
          <button type="button" onClick={send} disabled={busy} data-testid={`${testidPrefix}-resend-otp`}
            className="text-sm text-ink/50 hover:text-ink underline underline-offset-4">Resend</button>
        </div>
      )}
      {error && <p className="text-xs text-red-500" data-testid={`${testidPrefix}-otp-error`}>{error}</p>}
    </div>
  );
};

// variant: "society" | "commercial" | "advertiser" | "contact"
export const LeadForm = ({ variant = "contact", title, note, ctaLabel = "Submit", testidPrefix = "lead" }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [config, setConfig] = useState({ email_otp_required: true, mobile_otp_required: false });
  const [emailToken, setEmailToken] = useState("");
  const [mobileToken, setMobileToken] = useState("");

  useEffect(() => {
    axios.get(`${API}/forms/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const set = (e) => {
    let v = e.target.value;
    if (e.target.name === "phone") v = v.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, [e.target.name]: v }));
    if (e.target.name === "email") setEmailToken("");
    if (e.target.name === "phone") setMobileToken("");
  };

  const hasEmail = variant !== "society";
  const emailValid = !hasEmail || EMAIL_RE.test(form.email || "");
  const phoneValid = PHONE_RE.test(form.phone || "");
  const emailVerified = !hasEmail || !config.email_otp_required || !!emailToken;
  const mobileVerified = !config.mobile_otp_required || !!mobileToken;

  const submit = async (e) => {
    e.preventDefault();
    if (hasEmail && !emailValid) { toast.error("Enter a valid email address"); return; }
    if (!phoneValid) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (!emailVerified || !mobileVerified) { toast.error("Please verify your email and mobile first"); return; }
    setLoading(true);
    try {
      let recaptcha_token = "";
      if (SITE_KEY) {
        try { recaptcha_token = await getRecaptchaToken(SITE_KEY, `lead_${variant}`); } catch { /* backend decides */ }
      }
      let payload;
      if (variant === "society") {
        payload = {
          lead_type: "society",
          name: form.contact_person || "",
          phone: form.phone,
          organization: form.society_name,
          city: form.city,
          details: { locality: form.locality || "", towers: form.towers || "", flats: form.flats || "", role: form.role || "" },
        };
      } else if (variant === "commercial") {
        payload = {
          lead_type: "commercial",
          name: form.contact_person || "",
          email: form.email || "",
          phone: form.phone,
          organization: form.building_name,
          city: form.city,
          details: { locality: form.locality || "", floors: form.floors || "", footfall: form.footfall || "" },
        };
      } else if (variant === "advertiser") {
        payload = {
          lead_type: "advertiser",
          name: form.contact_person || "",
          email: form.email || "",
          phone: form.phone,
          organization: form.business_name,
          city: form.city,
          details: { business_type: form.business_type || "", budget_range: form.budget_range || "" },
        };
      } else {
        payload = {
          lead_type: "contact",
          name: form.name || "",
          email: form.email || "",
          phone: form.phone,
          message: form.message,
        };
      }
      payload.email_token = emailToken;
      payload.mobile_token = mobileToken;
      payload.recaptcha_token = recaptcha_token;
      await axios.post(`${API}/leads`, payload);
      window.umami?.track("Lead Submit", { form: variant });
      if (variant === "society") window.dataLayer?.push({ event: "society_enquiry_submit" });
      if (variant === "advertiser") window.dataLayer?.push({ event: "advertiser_enquiry_submit" });
      if (variant === "commercial") window.dataLayer?.push({ event: "commercial_enquiry_submit" });
      setDone(true);
      toast.success("Thanks - we'll be in touch shortly.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testidPrefix}-success`} className="bg-white border border-ink/10 rounded-xl p-10 text-center">
        <CheckCircle2 className="h-12 w-12 text-teal mx-auto" strokeWidth={1.5} />
        <h3 className="mt-6 font-display font-700 text-2xl">Request received.</h3>
        <p className="mt-3 text-ink/60 max-w-sm mx-auto">
          Thanks for reaching out to AdX. A member of our team will get back to you shortly.
        </p>
      </div>
    );
  }

  const emailField = (
    <Field label="Email" name="email" type="email" required value={form.email || ""} onChange={set}
      testid={`${testidPrefix}-email`}
      extra={config.email_otp_required && (
        <OtpBlock channel="email" target={(form.email || "").toLowerCase()} valid={emailValid}
          verified={!!emailToken} onVerified={setEmailToken} testidPrefix={`${testidPrefix}-email`} />
      )} />
  );
  const phoneField = (
    <Field label="Mobile number" name="phone" type="tel" required value={form.phone || ""} onChange={set}
      inputMode="numeric" maxLength={10} placeholder="10-digit mobile" testid={`${testidPrefix}-phone`}
      hint="10 digits, starting with 6, 7, 8 or 9"
      extra={config.mobile_otp_required && (
        <OtpBlock channel="mobile" target={form.phone || ""} valid={phoneValid}
          verified={!!mobileToken} onVerified={setMobileToken} testidPrefix={`${testidPrefix}-phone`} />
      )} />
  );

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="bg-white border border-ink/10 rounded-xl p-6 md:p-10">
      {title && <h3 className="font-display font-700 text-2xl">{title}</h3>}
      {note && <p className="mt-2 text-ink/55">{note}</p>}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        {variant === "society" && (
          <>
            <Field label="Society name" name="society_name" required value={form.society_name || ""} onChange={set} testid={`${testidPrefix}-society_name`} />
            <Field label="City" name="city" required value={form.city || ""} onChange={set} testid={`${testidPrefix}-city`} />
            <Field label="Locality" name="locality" value={form.locality || ""} onChange={set} testid={`${testidPrefix}-locality`} />
            <Field label="Towers" name="towers" value={form.towers || ""} onChange={set} testid={`${testidPrefix}-towers`} />
            <Field label="Approx. flats" name="flats" value={form.flats || ""} onChange={set} testid={`${testidPrefix}-flats`} />
            <Field label="Contact person" name="contact_person" required value={form.contact_person || ""} onChange={set} testid={`${testidPrefix}-contact_person`} />
            <Field label="Role" name="role" as="select" options={["President", "Secretary", "Member", "Facility Manager", "Resident", "Builder", "Others"]} required value={form.role || ""} onChange={set} testid={`${testidPrefix}-role`} />
            {phoneField}
          </>
        )}
        {variant === "commercial" && (
          <>
            <Field label="Building / property name" name="building_name" required value={form.building_name || ""} onChange={set} testid={`${testidPrefix}-building_name`} />
            <Field label="City" name="city" required value={form.city || ""} onChange={set} testid={`${testidPrefix}-city`} />
            <Field label="Locality" name="locality" value={form.locality || ""} onChange={set} testid={`${testidPrefix}-locality`} />
            <Field label="Floors / towers" name="floors" value={form.floors || ""} onChange={set} testid={`${testidPrefix}-floors`} />
            <Field label="Approx. daily footfall" name="footfall" value={form.footfall || ""} onChange={set} testid={`${testidPrefix}-footfall`} />
            <Field label="Contact person" name="contact_person" required value={form.contact_person || ""} onChange={set} testid={`${testidPrefix}-contact_person`} />
            {phoneField}
            {emailField}
          </>
        )}
        {variant === "advertiser" && (
          <>
            <Field label="Business name" name="business_name" required value={form.business_name || ""} onChange={set} testid={`${testidPrefix}-business_name`} />
            <Field label="Business type" name="business_type" as="select" options={["Local business", "Brand", "Agency"]} required value={form.business_type || ""} onChange={set} testid={`${testidPrefix}-business_type`} />
            <Field label="City" name="city" required value={form.city || ""} onChange={set} testid={`${testidPrefix}-city`} />
            <Field label="Budget range" name="budget_range" as="select" options={["Under ₹25k", "₹25k–₹1L", "₹1L–₹5L", "₹5L+"]} value={form.budget_range || ""} onChange={set} testid={`${testidPrefix}-budget_range`} />
            <Field label="Contact person" name="contact_person" required value={form.contact_person || ""} onChange={set} testid={`${testidPrefix}-contact_person`} />
            {phoneField}
            {emailField}
          </>
        )}
        {variant === "contact" && (
          <>
            <Field label="Your name" name="name" required value={form.name || ""} onChange={set} testid={`${testidPrefix}-name`} />
            {phoneField}
            {emailField}
            <div className="sm:col-span-2">
              <Field label="Message" name="message" as="textarea" value={form.message || ""} onChange={set} testid={`${testidPrefix}-message`} />
            </div>
          </>
        )}
      </div>

      <div className="mt-8">
        <button type="submit" disabled={loading || (config.email_otp_required && !emailVerified) || (config.mobile_otp_required && !mobileVerified)}
          data-testid={`${testidPrefix}-submit`}
          className="group inline-flex items-center justify-center gap-2 font-display font-600 rounded-md px-6 py-3.5 bg-ink text-white hover:bg-ink-700 transition-colors disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Sending…" : ctaLabel}
        </button>
        <p className="mt-3 text-xs text-ink/40">Protected by reCAPTCHA. {hasEmail ? "Email and mobile are" : "Your mobile is"} verified by OTP before submission.</p>
      </div>
    </form>
  );
};
